<template>
  <div id="floating-unit-layer"></div>
</template>

<script>
import { FloatingUnitMapLayer, FloatingUnitMapSource } from "@/config/Map.js";
import { createNamespacedHelpers } from "vuex";
import mapboxgl from "mapbox-gl";
import * as turf from "@turf/turf";
import { PlotMixin } from "@/mixins";

// Store
const {
  mapActions: mapActionsOfLiveMap,
  mapState: mapStateOfLiveMap,
} = createNamespacedHelpers("liveMap");

export default {
  name: "FloatingUnitLayer",
  mixins: [PlotMixin],
  data() {
    return {
      intervalId: null,
      featureCollection: null,
    };
  },
  props: {
    map: Object,
    isReady: { type: Boolean, default: false },
    mapEvent: String,
  },
  computed: {
    ...mapStateOfLiveMap({
      floatingUnits: state => state.floatingUnits,
      filterPlots: state => state.filterPlots,
    }),
  },
  watch: {
    async isReady() {
      if (this.isReady) {
        await this.addLayers();
        await this.getFloatingUnits();
        await this._setInterval();
      }
    },

    floatingUnits(floatingUnits) {
      this.setFloatingUnitsSource(floatingUnits);
    },

    filterPlots(filterPlots) {
      this.setLayerVisibility(filterPlots);
    },
  },
  methods: {
    ...mapActionsOfLiveMap(["getFloatingUnits"]),

    async addLayers() {
      this.map.addSource(FloatingUnitMapSource.FloatingUnitPointSource.id, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      this.map.addLayer({
        id: FloatingUnitMapLayer.FloatingUnitSymbolLayer.id,
        type: "symbol",
        source: FloatingUnitMapSource.FloatingUnitPointSource.id,
        minzoom: 8,
        maxzoom: 19,
        layout: {
          "icon-ignore-placement": true,
          "icon-allow-overlap": true,
          "icon-anchor": "bottom",
          "icon-image": "{icon}",
          "text-field": "{label}",
          "text-anchor": "top",
          "text-size": 14,
          "text-allow-overlap": true,
          "text-ignore-placement": true,
        },
        paint: {
          "text-color": "#FFFFFF",
          "text-halo-color": "#000000",
          "text-halo-width": 1,
        },
      });
    },

    setPopup() {
      const popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
      });

      this.map.on(
        "mouseenter",
        FloatingUnitMapLayer.FloatingUnitSymbolLayer.id,
        e => {
          this.map.getCanvas().style.cursor = "pointer";
          const coordinates = e.features[0].geometry.coordinates.slice();
          const description = e.features[0].properties;

          popup
            .setLngLat(coordinates)
            .setHTML(description)
            .setOffset(24)
            .addTo(this.map);
        }
      );

      this.map.on(
        "mouseleave",
        FloatingUnitMapLayer.FloatingUnitSymbolLayer.id,
        () => {
          this.map.getCanvas().style.cursor = "";
          popup.remove();
        }
      );
    },

    setFloatingUnitsSource(floatingUnits) {
      let features = [];
      floatingUnits.forEach(data => {
        if (data.location == null) {
          return;
        }
        let props = { icon: "flu-buoy", label: data.label || "UNKNOWN" };
        let point = turf.point(
          [data.location.longitude, data.location.latitude],
          props
        );
        features.push(point);
      });
      const floatingUnitPointSource = this.map.getSource(
        FloatingUnitMapSource.FloatingUnitPointSource.id
      );
      floatingUnitPointSource.setData(turf.featureCollection(features));
    },

    setLayerVisibility(filterPlots) {
      if (filterPlots.includes(String(this.plotParentCategory.ATTENTION))) {
        this.map.setLayoutProperty(
          FloatingUnitMapLayer.FloatingUnitSymbolLayer.id,
          "visibility",
          "visible"
        );
      } else {
        this.map.setLayoutProperty(
          FloatingUnitMapLayer.FloatingUnitSymbolLayer.id,
          "visibility",
          "none"
        );
      }
    },

    _setInterval() {
      this._clearInterval();
      this.intervalId = this.$setInterval(this.getFloatingUnits, 60000);
    },

    _clearInterval() {
      if (this.intervalId) {
        this.$clearInterval(this.intervalId);
        this.intervalId = null;
      }
    },
  },
  beforeDestroy() {
    this._clearInterval();
  },
};
</script>

<style></style>
